import axios from 'axios';
import { buscarFilaVendas, removerVendaDaFila, salvarMapeamentoPedidoSync, salvarVendaOffline } from './offlineStorage';
import { RESPONSE_ERROR_HANDLER_POSTPONE_HEADER } from './responseUtil';
import { URL } from './ambienteActions';
import { registrarSyncVendas } from '../registerServiceWorker';
import { getAuthentication } from '../security/securityContext';
import { isUserAuthenticated } from '../actions/util/authenticationJwtActions';

const SYNC_CONFIG = {
  headers: {
    [RESPONSE_ERROR_HANDLER_POSTPONE_HEADER]: true
  }
}

const MAX_TENTATIVAS_POLLING = 360; // 360 × 10s = 1 hora

let sincronizando = false;
const pollingEmAndamento = new Set();

export async function sincronizarVendasOffline(dispatch) {
  if (sincronizando || !navigator.onLine || !isUserAuthenticated(getAuthentication())) return;

  let fila;
  try {
    fila = await buscarFilaVendas();
  } catch (e) {
    return;
  }

  if (!fila || fila.length === 0) return;

  sincronizando = true;
  try {
    for (const venda of fila) {
      if (venda.erroSync) continue;
      if (venda.statusServidor === 'PROCESSANDO') {
        // timeout pode ter sido perdido em reload de página — reinicia polling, se não houver um já em andamento
        if (!pollingEmAndamento.has(venda.idChaveOffline)) {
          agendarPollingStatus(venda.idChaveOffline, venda, dispatch, 0);
        }
        continue;
      }
      await enviarVenda(venda, dispatch);
    }
  } finally {
    sincronizando = false;
  }
}

async function enviarVenda(venda, dispatch) {
  try {
    const dv = venda.dadosViagem || {}
    const ida = dv.ida || {}
    const dataViagem = ida.dataPartida
      ? [ida.dataPartida, ida.horaPartida].filter(Boolean).join(' ')
      : null

    const response = await axios.post(`${URL}/reserva/venda-offline`, {
      idChaveOffline: venda.idChaveOffline,
      dadosReserva: venda,
      origemNome: dv.origemNome || null,
      destinoNome: dv.destinoNome || null,
      dataViagem,
    }, SYNC_CONFIG);

    const { idChaveOffline } = response.data;

    await salvarVendaOffline({ ...venda, statusServidor: 'PROCESSANDO' });

    agendarPollingStatus(idChaveOffline, venda, dispatch, 0);

  } catch (erro) {
    if (!erro.response) return;
    const status = erro.response.status;
    if (status === 401) return; // sessão expirada: tenta de novo após reautenticação
    if (status >= 400 && status < 500) {
      const violacoes = erro.response.data && erro.response.data.violacoes;
      const mensagemErro = violacoes && violacoes.length
        ? violacoes.map(v => v.mensagem).join(', ')
        : 'Erro HTTP ' + status;
      await salvarVendaOffline({ ...venda, erroSync: mensagemErro, erroSyncStatus: status });
    }
  }
}

function agendarPollingStatus(idChaveOffline, venda, dispatch, tentativa) {
  if (tentativa === 0) {
    pollingEmAndamento.add(idChaveOffline);
  }

  if (tentativa >= MAX_TENTATIVAS_POLLING) {
    pollingEmAndamento.delete(idChaveOffline);
    salvarVendaOffline({ ...venda, erroSync: 'Timeout aguardando processamento no servidor' });
    return;
  }

  setTimeout(async () => {
    try {
      const response = await axios.get(
        `${URL}/reserva/venda-offline/${idChaveOffline}/status`,
        SYNC_CONFIG
      );
      const { status, pedidoId, erroMensagem } = response.data;

      if (status === 'RESOLVIDO') {
        pollingEmAndamento.delete(idChaveOffline);
        salvarMapeamentoPedidoSync(idChaveOffline, pedidoId);
        await removerVendaDaFila(idChaveOffline);
        if (dispatch) dispatch({ type: 'VENDAS_OFFLINE_ENVIADAS', payload: idChaveOffline });

      } else if (status === 'ERRO_SYNC') {
        pollingEmAndamento.delete(idChaveOffline);
        await salvarVendaOffline({ ...venda, erroSync: erroMensagem || 'Erro no processamento' });

      } else {
        agendarPollingStatus(idChaveOffline, venda, dispatch, tentativa + 1);
      }
    } catch (erro) {
      if (erro.response && erro.response.status === 401) {
        pollingEmAndamento.delete(idChaveOffline);
        return; // sessão expirada: a próxima sincronização retoma o polling
      }
      agendarPollingStatus(idChaveOffline, venda, dispatch, tentativa + 1);
    }
  }, 10 * 1000);
}

export function inicializarEventosSyncOffline(dispatch) {
  window.addEventListener('online', () => sincronizarVendasOffline(dispatch));

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      sincronizarVendasOffline(dispatch);
    }
  });

  window.addEventListener('pageshow', () => sincronizarVendasOffline(dispatch));
  window.addEventListener('focus', () => sincronizarVendasOffline(dispatch));

  setInterval(() => sincronizarVendasOffline(dispatch), 30000);

  sincronizarVendasOffline(dispatch);
}

export { registrarSyncVendas };
